// web/shims/ReactNativeSvgShim.ts
//
// Problem:
// - Some libraries (notably `react-native-gifted-charts`) pass RN touch/responder props like
//   `onPressOut` to `react-native-svg` elements.
// - On web, `react-native-svg`'s web `prepare()` forwards these props to the actual DOM nodes and
//   also injects RN responder handlers (`onResponder*`, `onStartShouldSetResponder`).
// - React DOM warns: "Unknown event handler property ... It will be ignored."
//
// Fix (web-only):
// - Prevent `react-native-svg` web from treating SVG nodes as "touchable" (disables mixin + responder injection).
// - Strip RN touch/responder props before they reach the DOM.
//
// This shim is activated via webpack alias in `web/webpack.config.js`.

type AnyObj = Record<string, any>;

// Patch `hasTouchableProperty` BEFORE loading the main module so WebShape constructor won't attach
// SvgTouchableMixin (which adds responder handlers).
try {
  // NOTE: webpack alias is `react-native-svg$` (exact match), so deep imports bypass the alias
  // and load the real package.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const hasPropertyMod = require('react-native-svg/lib/commonjs/web/utils/hasProperty.js');
  if (
    hasPropertyMod &&
    typeof hasPropertyMod.hasTouchableProperty === 'function'
  ) {
    hasPropertyMod.hasTouchableProperty = () => false;
  }
} catch {
  // ignore - if the internal path changes, worst case is we fall back to default behavior
}

// Patch `prepare()` to strip RN-only touch/responder props from SVG DOM props on web.
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const prepareMod = require('react-native-svg/lib/commonjs/web/utils/prepare.js');
  const originalPrepare = prepareMod?.prepare;
  if (typeof originalPrepare === 'function') {
    prepareMod.prepare = (self: any, props?: AnyObj) => {
      const p: AnyObj = props ?? self?.props ?? {};
      if (!p || typeof p !== 'object') return originalPrepare(self, p);

      // Drop RN-only responder/touch handlers that React DOM doesn't understand.
      // Keep `onPress` (it becomes `onClick` in react-native-svg's web prepare()).
      // Note: we intentionally remove `onPressIn/out` + long press, since those would otherwise
      //       either be forwarded to DOM or trigger responder injection.
      const {
        onStartShouldSetResponder,
        onStartShouldSetResponderCapture,
        onMoveShouldSetResponder,
        onMoveShouldSetResponderCapture,
        onResponderTerminationRequest,
        onResponderGrant,
        onResponderMove,
        onResponderRelease,
        onResponderTerminate,
        onResponderEnd,
        onPressIn,
        onPressOut,
        onLongPress,
        delayLongPress,
        ...rest
      } = p;

      return originalPrepare(self, rest);
    };
  }
} catch {
  // ignore
}

// Finally export the real package.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const RealSvg = require('react-native-svg/lib/commonjs/index.js');

module.exports = RealSvg;
module.exports.default = RealSvg?.default ?? RealSvg;
